import React, { useState, useEffect, useCallback } from 'react';
import { getTipoCuentaById, createTipoCuenta, updateTipoCuenta } from '../services/tipoCuentaService';
import { getPlanCuentas } from '../services/planCuentaService';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, ArrowLeft, RefreshCw, Calculator, Search, Check, AlertCircle, PlusCircle, FileText } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

// Modal de Búsqueda de Cuentas Contables (Optimizado)
const AccountSearchModal = ({ isOpen, onClose, onSelect, title, currentAccountId }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const { token } = useAuth();

    const fetchAccounts = useCallback(async () => {
        if (!isOpen) return;
        try {
            setLoading(true);
            const data = await getPlanCuentas(token, searchTerm);
            setAccounts(data.slice(0, 50));
        } catch (error) {
            console.error('Error fetching accounts:', error);
        } finally {
            setLoading(false);
        }
    }, [token, searchTerm, isOpen]);

    useEffect(() => {
        const timer = setTimeout(fetchAccounts, 300);
        return () => clearTimeout(timer);
    }, [fetchAccounts]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-[2px] p-4">
            <div className="bg-white shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-300 rounded-sm">
                <div className="bg-slate-800 p-3 flex justify-between items-center text-white">
                    <h3 className="text-xs font-bold uppercase flex items-center gap-2 tracking-widest">
                        <Calculator className="w-4 h-4 text-blue-400" />
                        {title}
                    </h3>
                    <button onClick={onClose} className="hover:text-red-400 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-3 bg-slate-50 border-b border-slate-200">
                    <div className="relative">
                        <input
                            autoFocus
                            type="text"
                            placeholder="Filtrar por código o nombre..."
                            className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-none focus:ring-1 focus:ring-blue-500 outline-none text-sm font-medium"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Search className="absolute left-3 top-2.5 text-slate-400 w-4 h-4" />
                    </div>
                </div>

                <div className="max-h-[350px] overflow-y-auto">
                    {loading ? (
                        <div className="p-10 text-center"><RefreshCw className="animate-spin mx-auto w-6 h-6 text-blue-500" /></div>
                    ) : accounts.length === 0 ? (
                        <div className="p-10 text-center text-slate-400 italic text-sm font-bold uppercase tracking-tighter">No se encontraron cuentas.</div>
                    ) : (
                        <table className="w-full text-left text-[12px]">
                            <thead className="bg-slate-100 sticky top-0 text-[10px] uppercase text-slate-500 font-black border-b border-slate-200">
                                <tr>
                                    <th className="px-4 py-2 tracking-widest w-32">Código</th>
                                    <th className="px-4 py-2 tracking-widest">Nombre de Cuenta</th>
                                    <th className="px-1 py-1 w-10"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {accounts.map(acc => (
                                    <tr
                                        key={acc.id}
                                        className={`border-b border-slate-100 hover:bg-blue-50 cursor-pointer transition-colors ${acc.id === currentAccountId ? 'bg-blue-100' : ''}`}
                                        onClick={() => onSelect(acc)}
                                    >
                                        <td className="px-4 py-2.5 font-mono font-bold text-blue-700">{acc.codigoIncoop}</td>
                                        <td className="px-4 py-2.5 text-slate-700 font-bold uppercase">{acc.nombreCuenta}</td>
                                        <td className="px-4 py-2.5 text-center">
                                            {acc.id === currentAccountId ? <Check className="w-4 h-4 text-green-600" /> : <PlusCircle className="w-4 h-4 text-slate-300" />}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <div className="p-2 bg-slate-50 border-t border-slate-200 text-right">
                    <button onClick={onClose} className="text-[10px] font-black uppercase text-slate-500 hover:text-slate-800 px-4 py-1">Cerrar</button>
                </div>
            </div>
        </div>
    );
};

const TipoCuentaForm = () => {
    const { id } = useParams();
    const isEditing = !!id;
    const navigate = useNavigate();
    const { token } = useAuth();

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEditing);

    const [form, setForm] = useState({
        nombre: '',
        codigo: '',
        descripcion: '',
        cuentaContablePasivoId: null,
        cuentaContablePasivoNombre: '',
        cuentaContablePasivoCodigo: '',
        inscripcion: 'N',
        aprobacion: 'N'
    });

    const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);

    const fetchTipoCuenta = useCallback(async () => {
        if (!isEditing) return;
        try {
            setFetching(true);
            const data = await getTipoCuentaById(token, id);
            setForm({
                nombre: data.nombre,
                codigo: data.codigo,
                descripcion: data.descripcion || '',
                cuentaContablePasivoId: data.cuentaContablePasivoId,
                cuentaContablePasivoNombre: data.cuentaContablePasivoNombre || '',
                cuentaContablePasivoCodigo: data.cuentaContablePasivoCodigo || '',
                inscripcion: data.inscripcion || 'N',
                aprobacion: data.aprobacion || 'N'
            });
        } catch (error) {
            toast.error('Error al cargar datos del tipo de cuenta');
            navigate('/parametrizacion/tipos-cuenta');
        } finally {
            setFetching(false);
        }
    }, [id, isEditing, token, navigate]);

    useEffect(() => {
        fetchTipoCuenta();
    }, [fetchTipoCuenta]);

    const handleSelectAccount = (account) => {
        setForm(prev => ({
            ...prev,
            cuentaContablePasivoId: account.id,
            cuentaContablePasivoNombre: account.nombreCuenta,
            cuentaContablePasivoCodigo: account.codigoIncoop
        }));
        setIsAccountModalOpen(false);
        toast.success('Cuenta contable vinculada');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.nombre.trim() || !form.codigo.trim()) {
            toast.error('Nombre y Código son obligatorios');
            return;
        }

        try {
            setLoading(true);
            const dataToSend = {
                nombre: form.nombre,
                codigo: form.codigo.toUpperCase(),
                descripcion: form.descripcion,
                cuentaContablePasivoId: form.cuentaContablePasivoId,
                inscripcion: form.inscripcion,
                aprobacion: form.aprobacion
            };

            if (isEditing) {
                await updateTipoCuenta(token, id, dataToSend);
                toast.success('Tipo de cuenta actualizado');
            } else {
                await createTipoCuenta(token, dataToSend);
                toast.success('Tipo de cuenta creado con éxito');
            }
            navigate('/parametrizacion/tipos-cuenta');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error al guardar los cambios');
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
                <RefreshCw className="w-10 h-10 animate-spin text-blue-600" />
                <p className="text-slate-500 font-bold uppercase text-xs tracking-widest">Cargando configuración...</p>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-4 animate-in fade-in duration-500">
            <Toaster position="top-right" />

            <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/parametrizacion/tipos-cuenta')}
                        className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-600"
                    >
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-black text-slate-800 uppercase tracking-tight italic">
                            {isEditing ? 'Gestión de Tipo de Cuenta' : 'Nuevo Tipo de Cuenta'}
                        </h1>
                        <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">Parametrización de Ahorros / Pasivos</p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-10 bg-white p-8 border border-slate-200 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-right from-blue-600 to-indigo-600"></div>

                {/* Columna Izquierda: Información de Negocio */}
                <div className="space-y-6 text-sm font-medium text-slate-600">
                    <div className="border-b-2 border-slate-100 pb-2 mb-4">
                        <h3 className="text-sm font-black text-blue-700 uppercase tracking-widest flex items-center gap-2">
                            <PlusCircle className="w-4 h-4" />
                            Identificación del Producto
                        </h3>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                        <div className="space-y-1.5">
                            <label className="text-[10px] font-black uppercase tracking-tighter text-slate-400">Nombre del Tipo de Cuenta (Comercial)</label>
                            <input
                                type="text"
                                className="w-full p-2.5 bg-slate-50 border border-slate-300 focus:bg-white focus:ring-1 focus:ring-blue-500 outline-none transition-all uppercase font-bold text-slate-800 rounded-sm"
                                placeholder="Ej: AHORRO A LA VISTA"
                                value={form.nombre}
                                onChange={(e) => setForm({ ...form, nombre: e.target.value.toUpperCase() })}
                                required
                            />
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-[10px] font-black uppercase tracking-tighter text-slate-400">Código de Sistema (Único)</label>
                            <input
                                type="text"
                                className="w-full p-2.5 bg-slate-50 border border-slate-300 focus:bg-white focus:ring-1 focus:ring-blue-500 outline-none transition-all uppercase font-black text-blue-700 rounded-sm"
                                placeholder="Ej: AHO_VIS"
                                value={form.codigo}
                                onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })}
                                disabled={isEditing}
                                required
                            />
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-[10px] font-black uppercase tracking-tighter text-slate-400">Descripción Breve</label>
                            <textarea
                                rows="3"
                                className="w-full p-2.5 bg-slate-50 border border-slate-300 focus:bg-white focus:ring-1 focus:ring-blue-500 outline-none transition-all text-slate-800 rounded-sm"
                                placeholder="Detalles operativos o legales del tipo de cuenta..."
                                value={form.descripcion}
                                onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                            />
                        </div>

                        {/* Generación Automática */}
                        <div className="pt-4 space-y-4">
                            <div className="border-b border-slate-100 pb-1">
                                <h4 className="text-[10px] font-black uppercase text-slate-400">Configuración de Generación Automática</h4>
                            </div>
                            
                            <div className="grid grid-cols-1 gap-4">
                                <label className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-100 transition-colors group">
                                    <input
                                        type="checkbox"
                                        className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        checked={form.inscripcion === 'S'}
                                        onChange={(e) => {
                                            const newValue = e.target.checked ? 'S' : 'N';
                                            setForm({ ...form, inscripcion: newValue });
                                            if (newValue === 'S') {
                                                toast("Fase de Inscripción: Esta cuenta se creará automáticamente cuando el socio complete el formulario de registro.", {
                                                    icon: '📝',
                                                    style: { borderRadius: '0', background: '#334155', color: '#fff', fontSize: '12px' }
                                                });
                                            }
                                        }}
                                    />
                                    <div>
                                        <div className="text-xs font-black text-slate-700 uppercase">¿Crear en fase de Inscripción?</div>
                                        <div className="text-[10px] text-slate-500 font-bold uppercase tracking-tight">Vínculo automático al registrar solicitud</div>
                                    </div>
                                </label>

                                <label className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-100 transition-colors group">
                                    <input
                                        type="checkbox"
                                        className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        checked={form.aprobacion === 'S'}
                                        onChange={(e) => {
                                            const newValue = e.target.checked ? 'S' : 'N';
                                            setForm({ ...form, aprobacion: newValue });
                                            if (newValue === 'S') {
                                                toast("Fase de Aprobación: Esta cuenta se creará automáticamente cuando el administrador apruebe o active al socio.", {
                                                    icon: '✅',
                                                    style: { borderRadius: '0', background: '#334155', color: '#fff', fontSize: '12px' }
                                                });
                                            }
                                        }}
                                    />
                                    <div>
                                        <div className="text-xs font-black text-slate-700 uppercase">¿Crear en fase de Aprobación?</div>
                                        <div className="text-[10px] text-slate-500 font-bold uppercase tracking-tight">Vínculo automático al activar al socio</div>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Columna Derecha: Configuración Contable */}
                <div className="space-y-6">
                    <div className="border-b-2 border-slate-100 pb-2 mb-4">
                        <h3 className="text-sm font-black text-rose-600 uppercase tracking-widest flex items-center gap-2">
                            <Calculator className="w-4 h-4" />
                            Configuración de Enlace Contable
                        </h3>
                    </div>

                    <div className="bg-slate-50 border border-slate-200 p-6 space-y-6 rounded-sm">
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-blue-100 text-blue-700 rounded-lg">
                                <AlertCircle className="w-6 h-6" />
                            </div>
                            <div className="text-xs text-slate-500 leading-relaxed font-bold italic uppercase">
                                Nota del Administrador: La cuenta contable definida aquí se utilizará automáticamente para el registro de los saldos de depósitos de este tipo de cuenta.
                            </div>
                        </div>

                        <div className="pt-4 space-y-4">
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold uppercase text-slate-500 tracking-tighter">Cuenta Contable de Pasivo (Obligaciones)</label>
                                <div className="flex gap-2">
                                    <div className={`flex-1 p-3 border ${form.cuentaContablePasivoId ? 'border-blue-200 bg-blue-50' : 'border-slate-300 bg-white'} min-h-[50px] flex items-center gap-3 rounded-sm`}>
                                        <FileText className={`w-5 h-5 ${form.cuentaContablePasivoId ? 'text-blue-600' : 'text-slate-300'}`} />
                                        <div className="flex-1 overflow-hidden">
                                            {form.cuentaContablePasivoId ? (
                                                <>
                                                    <div className="text-[11px] font-black text-blue-700">{form.cuentaContablePasivoCodigo}</div>
                                                    <div className="text-[10px] text-slate-600 uppercase font-black truncate">{form.cuentaContablePasivoNombre}</div>
                                                </>
                                            ) : (
                                                <div className="text-xs text-slate-400 italic">No se ha vinculado ninguna cuenta</div>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setIsAccountModalOpen(true)}
                                        className="px-4 bg-slate-800 text-white hover:bg-slate-700 transition-all flex items-center justify-center font-bold text-xs uppercase rounded-sm"
                                    >
                                        Vincular
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="pt-10 flex gap-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-4 flex items-center justify-center gap-2 transition-all shadow-lg active:scale-95 disabled:opacity-50 font-black text-sm uppercase rounded-sm"
                        >
                            {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                            {isEditing ? 'Confirmar Actualización' : 'Guardar Nuevo Tipo'}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/parametrizacion/tipos-cuenta')}
                            className="px-8 border-2 border-slate-200 hover:border-slate-800 hover:bg-slate-800 hover:text-white transition-all text-slate-500 font-bold text-sm uppercase rounded-sm"
                        >
                            <X className="w-5 h-5 mx-auto" />
                        </button>
                    </div>
                </div>
            </form>

            <AccountSearchModal
                isOpen={isAccountModalOpen}
                onClose={() => setIsAccountModalOpen(false)}
                onSelect={handleSelectAccount}
                title="Buscador: Cuentas Contables de Pasivo"
                currentAccountId={form.cuentaContablePasivoId}
            />
        </div>
    );
};

export default TipoCuentaForm;
